/**
 * Prediction Analytics Service
 *
 * Advanced analytics functions for demand forecast accuracy analysis.
 * Provides database function wrappers and complex calculations.
 *
 * Part of US-FORECAST-CALIBRATION Step 7
 * Updated: Now uses unified demand_forecasts table (US-FORECAST-UNIFIED)
 */

import { supabase } from '@/supabase-client'
import { calculateMAPE, calculateBias } from './forecastStatistics'
import { toLocalDateString } from '../utils/dateUtils'

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get Monday of the week containing the given date
 * @param {string} dateStr - Date string (YYYY-MM-DD)
 * @returns {string} Monday date in YYYY-MM-DD format
 */
function getWeekStart(dateStr) {
  const d = new Date(dateStr)
  const day = d.getDay()
  const diff = day === 0 ? -6 : 1 - day
  d.setDate(d.getDate() + diff)
  return toLocalDateString(d)
}

// =============================================================================
// Rolling Statistics (Database Function Wrappers)
// =============================================================================

/**
 * Get rolling statistics for a specific strain from the database
 *
 * @param {string} strainId - Strain UUID
 * @param {number} [weeks=26] - Number of weeks for rolling window
 * @returns {Promise<Object|null>} Rolling statistics for the strain
 *
 * @example
 * const stats = await getStrainRollingStats('uuid-here', 26)
 * // Returns: { strain_id, strain_name, avg_weekly_kg, stddev_weekly_kg, ... }
 */
export async function getStrainRollingStats(strainId, weeks = 26) {
  const { data, error } = await supabase
    .rpc('get_strain_rolling_statistics', {
      p_strain_id: strainId,
      p_weeks: weeks
    })

  if (error) {
    console.error('[predictionAnalytics] getStrainRollingStats error:', error)
    throw error
  }

  return data?.[0] || null
}

/**
 * Get rolling statistics for all strains from the database
 *
 * @param {number} [weeks=26] - Number of weeks for rolling window
 * @returns {Promise<Array>} Rolling statistics for all strains
 *
 * @example
 * const allStats = await getAllStrainRollingStats(26)
 * // Returns array of strain statistics
 */
export async function getAllStrainRollingStats(weeks = 26) {
  const { data, error } = await supabase
    .rpc('get_strain_rolling_statistics', {
      p_strain_id: null,
      p_weeks: weeks
    })

  if (error) {
    console.error('[predictionAnalytics] getAllStrainRollingStats error:', error)
    throw error
  }

  return data || []
}

// =============================================================================
// Calibration Impact Analysis
// =============================================================================

/**
 * Calculate calibration impact - comparing raw vs calibrated predictions
 *
 * Measures how much the calibration process improved prediction accuracy
 * by comparing pre-calibration (raw) vs post-calibration predictions.
 *
 * @param {Array} weeklyData - Weekly aggregated forecast data with actuals
 * @returns {Object|null} Calibration impact metrics
 *
 * @example
 * const impact = calculateCalibrationImpact(weeklyData)
 * // Returns: {
 * //   raw_mape: 0.45,
 * //   calibrated_mape: 0.18,
 * //   improvement: 0.27,
 * //   improvement_pct: 60,
 * //   sample_count: 12
 * // }
 */
export function calculateCalibrationImpact(weeklyData) {
  if (!weeklyData?.length) return null

  // Filter to only records with actuals filled in
  const withActuals = weeklyData.filter(w =>
    w.actualTotalKg != null &&
    w.rawTotalKg != null &&
    w.calibratedTotalKg != null
  )

  if (!withActuals.length) return null

  const rawPredictions = withActuals.map(w => w.rawTotalKg)
  const calibratedPredictions = withActuals.map(w => w.calibratedTotalKg)
  const actuals = withActuals.map(w => w.actualTotalKg)

  const rawMAPE = calculateMAPE(rawPredictions, actuals)
  const calibratedMAPE = calculateMAPE(calibratedPredictions, actuals)

  // Handle edge cases where MAPE couldn't be calculated
  if (rawMAPE == null || calibratedMAPE == null) {
    return {
      raw_mape: rawMAPE,
      calibrated_mape: calibratedMAPE,
      improvement: null,
      improvement_pct: null,
      sample_count: withActuals.length
    }
  }

  const improvement = rawMAPE - calibratedMAPE
  const improvementPct = rawMAPE > 0
    ? (improvement / rawMAPE) * 100
    : 0

  return {
    raw_mape: Math.round(rawMAPE * 10000) / 10000,
    calibrated_mape: Math.round(calibratedMAPE * 10000) / 10000,
    improvement: Math.round(improvement * 10000) / 10000,
    improvement_pct: Math.round(improvementPct * 10) / 10,
    sample_count: withActuals.length
  }
}

// =============================================================================
// Variance Analysis
// =============================================================================

/**
 * Get top variance contributors for a strain
 * Returns weekly periods with highest prediction variance.
 *
 * @param {string} strainId - Strain UUID
 * @param {number} [limit=10] - Maximum number of results
 * @returns {Promise<Array>} Weeks with highest prediction variance
 */
export async function getTopVarianceContributors(strainId, limit = 10) {
  // Fetch daily forecasts with actuals for this strain
  const { data, error } = await supabase
    .from('demand_forecasts')
    .select(`
      id,
      strain_id,
      forecast_date,
      recurring_kg,
      predicted_kg,
      calibrated_kg,
      actual_kg,
      calibration_factor,
      week_start,
      is_actual
    `)
    .eq('strain_id', strainId)
    .eq('is_actual', true)  // Only with actuals
    .order('forecast_date', { ascending: false })
    .limit(limit * 14)  // Get more days to aggregate into weeks

  if (error) {
    console.error('[predictionAnalytics] getTopVarianceContributors error:', error)
    throw error
  }

  if (!data || data.length === 0) return []

  // Aggregate by week
  const weekMap = new Map()

  for (const record of data) {
    const weekStart = record.week_start || getWeekStart(record.forecast_date)

    if (!weekMap.has(weekStart)) {
      weekMap.set(weekStart, {
        prediction_week: weekStart,
        calibratedTotalKg: 0,
        actualTotalKg: 0,
        calibrationFactorSum: 0,
        dayCount: 0
      })
    }

    const week = weekMap.get(weekStart)
    week.calibratedTotalKg += record.calibrated_kg || 0
    week.actualTotalKg += record.actual_kg || 0
    week.calibrationFactorSum += record.calibration_factor || 1
    week.dayCount += 1
  }

  // Calculate variance for each week
  const withVariance = [...weekMap.values()].map(week => ({
    prediction_week: week.prediction_week,
    predicted_kg: Math.round(week.calibratedTotalKg * 100) / 100,
    actual_kg: Math.round(week.actualTotalKg * 100) / 100,
    calibration_factor: week.calibrationFactorSum / week.dayCount,
    variance_kg: Math.round((week.calibratedTotalKg - week.actualTotalKg) * 100) / 100,
    variance_abs: Math.abs(week.calibratedTotalKg - week.actualTotalKg),
    variance_pct: week.actualTotalKg > 0
      ? Math.round(((week.calibratedTotalKg - week.actualTotalKg) / week.actualTotalKg) * 10000) / 100
      : null
  }))

  // Sort by absolute variance (largest first) and limit
  return withVariance
    .sort((a, b) => b.variance_abs - a.variance_abs)
    .slice(0, limit)
}

// =============================================================================
// Prediction Trend Analysis
// =============================================================================

/**
 * Get prediction trend over time for a strain
 *
 * Shows how predictions, actuals, and calibration factors have evolved.
 * Aggregates daily forecasts into weekly data points.
 *
 * @param {string} strainId - Strain UUID
 * @param {number} [weeks=12] - Number of weeks of history
 * @returns {Promise<Array>} Weekly prediction records sorted chronologically
 *
 * @example
 * const trend = await getPredictionTrend('uuid', 12)
 * // Returns: [{ prediction_week, raw_predicted_kg, predicted_kg, actual_kg, ... }, ...]
 */
export async function getPredictionTrend(strainId, weeks = 12) {
  // Calculate date range
  const endDate = new Date()
  endDate.setHours(0, 0, 0, 0)
  const startDate = new Date(endDate)
  startDate.setDate(startDate.getDate() - (weeks * 7))

  const startDateStr = toLocalDateString(startDate)
  const endDateStr = toLocalDateString(endDate)

  const { data, error } = await supabase
    .from('demand_forecasts')
    .select(`
      id,
      forecast_date,
      recurring_kg,
      predicted_kg,
      calibrated_kg,
      actual_kg,
      calibration_factor,
      is_actual,
      week_start
    `)
    .eq('strain_id', strainId)
    .gte('forecast_date', startDateStr)
    .lte('forecast_date', endDateStr)
    .order('forecast_date', { ascending: true })

  if (error) {
    console.error('[predictionAnalytics] getPredictionTrend error:', error)
    throw error
  }

  if (!data || data.length === 0) return []

  // Aggregate by week
  const weekMap = new Map()

  for (const record of data) {
    const weekStart = record.week_start || getWeekStart(record.forecast_date)

    if (!weekMap.has(weekStart)) {
      weekMap.set(weekStart, {
        prediction_week: weekStart,
        rawTotalKg: 0,
        calibratedTotalKg: 0,
        actualTotalKg: 0,
        hasActuals: false,
        calibrationFactorSum: 0,
        dayCount: 0
      })
    }

    const week = weekMap.get(weekStart)
    week.rawTotalKg += (record.recurring_kg || 0) + (record.predicted_kg || 0)
    week.calibratedTotalKg += record.calibrated_kg || 0
    week.actualTotalKg += record.actual_kg || 0
    week.calibrationFactorSum += record.calibration_factor || 1
    week.dayCount += 1

    if (record.is_actual) {
      week.hasActuals = true
    }
  }

  // Convert to array and enrich with calculated fields
  const weeklyData = [...weekMap.values()]
    .sort((a, b) => a.prediction_week.localeCompare(b.prediction_week))
    .map(week => {
      const errorKg = week.hasActuals
        ? week.calibratedTotalKg - week.actualTotalKg
        : null

      const errorPct = week.hasActuals && week.actualTotalKg > 0
        ? ((week.calibratedTotalKg - week.actualTotalKg) / week.actualTotalKg) * 100
        : null

      return {
        prediction_week: week.prediction_week,
        raw_predicted_kg: Math.round(week.rawTotalKg * 100) / 100,
        predicted_kg: Math.round(week.calibratedTotalKg * 100) / 100,
        actual_kg: week.hasActuals ? Math.round(week.actualTotalKg * 100) / 100 : null,
        calibration_factor: Math.round((week.calibrationFactorSum / week.dayCount) * 1000) / 1000,
        error_kg: errorKg != null ? Math.round(errorKg * 100) / 100 : null,
        error_pct: errorPct != null ? Math.round(errorPct * 10) / 10 : null
      }
    })

  return weeklyData
}

// =============================================================================
// Summary Statistics
// =============================================================================

/**
 * Calculate comprehensive prediction statistics for a strain
 *
 * Provides all key metrics for the prediction model screen:
 * MAPE, bias, band accuracy, and calibration impact.
 *
 * @param {string} strainId - Strain UUID
 * @param {number} [weeks=12] - Number of weeks to analyse
 * @returns {Promise<Object>} Comprehensive statistics
 *
 * @example
 * const stats = await getPredictionSummaryStats('uuid', 12)
 */
export async function getPredictionSummaryStats(strainId, weeks = 12) {
  // Fetch trend data
  const trend = await getPredictionTrend(strainId, weeks)

  if (!trend || trend.length === 0) {
    return {
      has_data: false,
      sample_count: 0
    }
  }

  // Filter to records with actuals
  const withActuals = trend.filter(r => r.actual_kg != null)

  if (withActuals.length === 0) {
    return {
      has_data: true,
      has_actuals: false,
      sample_count: trend.length,
      predictions_pending: trend.length
    }
  }

  const predictions = withActuals.map(r => r.predicted_kg)
  const actuals = withActuals.map(r => r.actual_kg)
  const rawPredictions = withActuals.map(r => r.raw_predicted_kg)

  // Core metrics
  const mape = calculateMAPE(predictions, actuals)
  const bias = calculateBias(predictions, actuals)
  const rawMape = calculateMAPE(rawPredictions, actuals)

  // Most recent comparison
  const latest = withActuals[withActuals.length - 1]
  const currentVsTarget = latest ? {
    prediction_week: latest.prediction_week,
    predicted: latest.predicted_kg,
    actual: latest.actual_kg,
    variance: latest.error_kg,
    variance_pct: latest.error_pct
  } : null

  return {
    has_data: true,
    has_actuals: true,
    sample_count: withActuals.length,
    predictions_pending: trend.length - withActuals.length,
    mape,
    bias,
    raw_mape: rawMape,
    calibration_improvement: rawMape != null && mape != null
      ? rawMape - mape
      : null,
    current_vs_target: currentVsTarget,
    trend
  }
}

// =============================================================================
// Exports
// =============================================================================

export default {
  getStrainRollingStats,
  getAllStrainRollingStats,
  calculateCalibrationImpact,
  getTopVarianceContributors,
  getPredictionTrend,
  getPredictionSummaryStats
}
